import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';

interface Kamoku {
    id: number;
    kamoku_code: string;
    kamoku: string;
    kamoku_yomi?: string; // Optional kana reading
}

interface KamokuSelectProps {
    kamokuList: Kamoku[];
    value: number | null;
    onChange: (value: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
    style?: React.CSSProperties;
    placeholder?: string;
    autoFocus?: boolean;
}

const KamokuSelect: React.FC<KamokuSelectProps> = ({
    kamokuList,
    value,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    style,
    placeholder = '選択してください',
    autoFocus = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const isSelectingRef = useRef(false);

    // Initialize searchTerm based on current value
    useEffect(() => {
        if (value !== null) {
            const selected = kamokuList.find((k) => k.id != null && value != null && String(k.id) === String(value));
            if (selected) {
                // When a value is selected, we generally show its name or code.
                // However, if the user is not editing, we might just want to show the formatted label.
                // But since this is an input-based select, we update the search term to match the selection
                // ONLY if the menu is closed. If open, we want to keep the user's typed search.
                if (!isOpen) {
                    setSearchTerm(`${selected.kamoku_code} - ${selected.kamoku}`);
                }
            }
        } else if (!isOpen) { // Clear if null and closed
            setSearchTerm('');
        }
    }, [value, kamokuList, isOpen]);


    const filteredOptions = useMemo(() => {
        if (!searchTerm) return kamokuList;

        // If the search term exactly matches the currently selected item's label, show all
        const selectedItem = kamokuList.find(k => k.id != null && value != null && String(k.id) === String(value));
        if (selectedItem) {
            const selectedLabel = `${selectedItem.kamoku_code} - ${selectedItem.kamoku}`;
            if (searchTerm === selectedLabel) {
                return kamokuList;
            }
        }

        const lowerSearch = searchTerm.toLowerCase();

        return kamokuList.filter((k) => {
            const code = k.kamoku_code.toLowerCase();
            const name = k.kamoku.toLowerCase();
            const yomi = (k.kamoku_yomi || '').toLowerCase();
            const label = `${k.kamoku_code} - ${k.kamoku}`.toLowerCase();

            return (
                code.includes(lowerSearch) ||
                name.includes(lowerSearch) ||
                yomi.includes(lowerSearch) ||
                label.includes(lowerSearch)
            );
        });
    }, [kamokuList, searchTerm, value]);

    // ドロップダウンを開いたときに、既に選択済みの場合はその行をハイライトする（Enterで先頭に置き換わらないようにする）
    useEffect(() => {
        if (isOpen && value !== null && filteredOptions.length > 0) {
            const idx = filteredOptions.findIndex((k) => k.id != null && value != null && String(k.id) === String(value));
            setHighlightedIndex(idx >= 0 ? idx : 0);
        } else if (isOpen && filteredOptions.length > 0) {
            setHighlightedIndex(0);
        }
    }, [isOpen, value, filteredOptions]);


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setIsOpen(true);
        setHighlightedIndex(0);
        // If the user clears the input, we should probably treat it as clearing the value?
        // Or wait for them to select/blur. Let's wait.
        if (e.target.value === '') {
            onChange(''); // Treat empty input as clearing the selection
        }
    };

    const handleSelect = (kamoku: Kamoku) => {
        isSelectingRef.current = true;
        onChange(kamoku.id.toString());
        setSearchTerm(`${kamoku.kamoku_code} - ${kamoku.kamoku}`);
        setIsOpen(false);
        // Reset the selecting flag after a short delay
        setTimeout(() => { isSelectingRef.current = false; }, 100);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === ' ') {
                setIsOpen(true);
                e.preventDefault();
                return;
            }
            // Enterキーはドロップダウンが閉じている時は親に伝播
            if (e.key === 'Enter' && onKeyDown) {
                onKeyDown(e);
                return;
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex((prev) =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (filteredOptions.length > 0) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                // Revert search term to selected value if cancelled?
                // Let's just close.
                break;
            case 'Tab':
                // Allow tab to move focus, but also select if something is highlighted?
                // Standard behavior is usually just move focus (and maybe select current match).
                // Let's close and optionally select if exact. For now just let it blur.
                setIsOpen(false);
                break;
        }
    };

    // Calculate dropdown position when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: Math.max(280, rect.width),
            });
        }
    }, [isOpen]);

    // Close on click outside (must also check portal-rendered dropdown)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isSelectingRef.current) return; // Don't close during selection
            const target = event.target as Node;
            // Check if click is inside the container OR inside the portal dropdown list
            const isInsideContainer = containerRef.current && containerRef.current.contains(target);
            const isInsideDropdown = listRef.current && listRef.current.contains(target);
            if (!isInsideContainer && !isInsideDropdown) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Scroll highlighted item into view
    useEffect(() => {
        if (isOpen && listRef.current) {
            const activeItem = listRef.current.children[highlightedIndex] as HTMLElement;
            if (activeItem) {
                // Simple scrollIntoView logic
                const listTop = listRef.current.scrollTop;
                const listHeight = listRef.current.clientHeight;
                const itemTop = activeItem.offsetTop;
                const itemHeight = activeItem.clientHeight;

                if (itemTop < listTop) {
                    listRef.current.scrollTop = itemTop;
                } else if (itemTop + itemHeight > listTop + listHeight) {
                    listRef.current.scrollTop = itemTop + itemHeight - listHeight;
                }
            }
        }
    }, [highlightedIndex, isOpen]);


    return (
        <div
            ref={containerRef}
            style={{ ...style, position: 'relative', display: 'inline-block' }}
        >
            <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={() => {
                    // When focused, select all text so user can immediately type to search
                    if (inputRef.current) {
                        inputRef.current.select();
                    }
                    setIsOpen(true);
                    if (onFocus) onFocus();
                }}
                onBlur={() => {
                    // Delay closing to allow clicks on options
                    // But we handle clicks via mousedown/touchstart usually or the click outside listener.
                    if (onBlur) onBlur();
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                style={{
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: '4px 4px',
                    border: '2px inset #c0c0c0', // Match classic style
                    fontFamily: 'MS UI Gothic, sans-serif',
                    fontSize: '14px', // Increased font size for better visibility
                    outline: 'none',
                }}
                autoFocus={autoFocus}
            />

            {isOpen && filteredOptions.length > 0 && createPortal(
                <ul
                    ref={listRef}
                    style={{
                        position: 'fixed',
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`,
                        width: `${dropdownPosition.width}px`,
                        maxHeight: '300px', // 高さを増やして多くの選択肢を表示
                        overflowY: 'auto',
                        margin: 0,
                        padding: 0,
                        listStyle: 'none',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        zIndex: 99999, // 最上位レイヤーに表示
                        boxShadow: '2px 2px 10px rgba(0,0,0,0.3)',
                    }}
                >
                    {filteredOptions.map((kamoku, index) => (
                        <li
                            key={kamoku.id}
                            onClick={() => handleSelect(kamoku)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            style={{
                                padding: '8px 10px', // パディングを増やして選択しやすく
                                cursor: 'pointer',
                                backgroundColor: index === highlightedIndex ? '#0066cc' : 'white',
                                color: index === highlightedIndex ? 'white' : 'black',
                                fontSize: '14px', // フォントサイズを大きく
                                fontFamily: 'MS UI Gothic, sans-serif',
                                lineHeight: '1.4', // 行間を広げる
                            }}
                        >
                            {kamoku.kamoku_code} - {kamoku.kamoku}
                            {kamoku.kamoku_yomi && <span style={{ fontSize: '11px', marginLeft: '8px', color: index === highlightedIndex ? '#e0e0e0' : '#888' }}>({kamoku.kamoku_yomi})</span>}
                        </li>
                    ))}
                </ul>,
                document.body
            )}
        </div>
    );
};

export default KamokuSelect;
